import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Star, Check, ArrowLeft, ShieldCheck, Truck } from "lucide-react";
import api from "../lib/api";
import { useCart } from "../context/CartContext";
import { Reveal } from "../components/Reveal";
import { ProductReviews } from "../components/ProductReviews";
import { DISCLAIMER } from "../data/content";
import { toast } from "sonner";

export default function ProductDetail() {
  const { slug } = useParams();
  const { addItem } = useCart();
  const [product, setProduct] = useState(null);
  const [size, setSize] = useState(null);
  const [variant, setVariant] = useState(null);
  const [activeImg, setActiveImg] = useState(0);

  useEffect(() => {
    api.get(`/products/${slug}`).then((r) => {
      setProduct(r.data);
      setSize(r.data.sizes[0]);
      setVariant(null);
      setActiveImg(0);
    });
  }, [slug]);

  if (!product) {
    return <div className="flex min-h-screen items-center justify-center font-mono text-sm text-muted">Loading…</div>;
  }

  const addToCart = () => {
    if (product.has_variants && !variant) {
      toast.error("Please select a shea variant first");
      return;
    }
    addItem({
      product_id: product.id,
      name: product.name,
      size: size.size,
      variant,
      price: size.price,
      image: product.image,
      quantity: 1,
    });
    toast.success(`${product.name} added to cart`);
  };

  const gallery = product.gallery?.length ? product.gallery : [product.image];

  return (
    <div className="pt-24" data-testid="product-detail">
      <div className="mx-auto max-w-[1400px] px-5 sm:px-8 py-8">
        <Link to="/shop" className="inline-flex items-center gap-2 font-mono text-xs uppercase tracking-wider text-muted hover:text-gold" data-testid="back-to-shop">
          <ArrowLeft className="h-4 w-4" /> Back to shop
        </Link>
      </div>

      <div className="mx-auto grid max-w-[1400px] gap-12 px-5 sm:px-8 pb-24 lg:grid-cols-2">
        {/* gallery */}
        <div className="lg:sticky lg:top-24 lg:self-start">
          <div className="relative overflow-hidden rounded-sm bg-cream-2 aspect-[4/5]">
            <motion.img
              key={activeImg}
              initial={{ opacity: 0, scale: 1.04 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.6, ease: [0.16, 1, 0.3, 1] }}
              src={gallery[activeImg]}
              alt={product.name}
              className="h-full w-full object-cover"
            />
          </div>
          {gallery.length > 1 && (
            <div className="mt-4 flex gap-3">
              {gallery.map((g, i) => (
                <button
                  key={i}
                  onClick={() => setActiveImg(i)}
                  className={`h-20 w-16 overflow-hidden rounded-sm border-2 ${i === activeImg ? "border-gold" : "border-transparent"}`}
                  data-testid={`gallery-thumb-${i}`}
                >
                  <img src={g} alt="" className="h-full w-full object-cover" />
                </button>
              ))}
            </div>
          )}
        </div>

        {/* info */}
        <div>
          <Reveal>
            <span className="rounded-full bg-cream-2 px-3 py-1 font-mono text-[10px] uppercase tracking-wider text-moss">{product.badge}</span>
            <h1 className="mt-5 font-serif text-4xl sm:text-5xl leading-[1.02] text-moss">{product.name}</h1>
            <p className="mt-3 font-serif text-xl italic text-gold">{product.tagline}</p>
            <div className="mt-4 flex items-center gap-3 text-sm">
              <span className="flex items-center gap-1 text-gold"><Star className="h-4 w-4 fill-gold" /> {product.rating}</span>
              <span className="text-muted">·</span>
              <span className="text-muted">{product.review_count} reviews</span>
            </div>

            <p className="mt-6 text-muted leading-relaxed">{product.description}</p>

            {/* variant */}
            {product.has_variants && (
              <div className="mt-8">
                <p className="font-mono text-xs uppercase tracking-wider text-moss mb-3">Choose your shea</p>
                <div className="flex flex-col gap-3 sm:flex-row">
                  {product.formula_variants.map((v) => (
                    <button
                      key={v}
                      onClick={() => setVariant(v)}
                      data-testid={`variant-${v.includes("Ivory") ? "ivory" : "nilotica"}`}
                      className={`flex-1 rounded-sm border px-4 py-3 text-left text-sm transition-colors ${
                        variant === v ? "border-gold bg-cream-2" : "border-[#E4DDD0] hover:border-gold"
                      }`}
                    >
                      <span className="flex items-center justify-between">
                        <span className="font-medium text-moss">{v}</span>
                        {variant === v && <Check className="h-4 w-4 text-gold" />}
                      </span>
                    </button>
                  ))}
                </div>
              </div>
            )}

            {/* size */}
            <div className="mt-8">
              <p className="font-mono text-xs uppercase tracking-wider text-moss mb-3">Size</p>
              <div className="flex gap-3">
                {product.sizes.map((s) => (
                  <button
                    key={s.sku}
                    onClick={() => setSize(s)}
                    data-testid={`size-${s.size.replace(/\s/g, "")}`}
                    className={`flex-1 rounded-sm border px-4 py-3 transition-colors ${
                      size?.sku === s.sku ? "border-gold bg-cream-2" : "border-[#E4DDD0] hover:border-gold"
                    }`}
                  >
                    <span className="block font-serif text-xl text-moss">{s.size}</span>
                    <span className="block text-sm text-gold">${s.price.toFixed(2)}</span>
                    {s.inventory <= 0 && <span className="text-[10px] text-terracotta font-mono">Sold out</span>}
                  </button>
                ))}
              </div>
            </div>

            <div className="mt-8 flex items-center gap-5">
              <span className="font-serif text-4xl text-moss" data-testid="detail-price">${size?.price.toFixed(2)}</span>
              <button
                onClick={addToCart}
                disabled={size?.inventory <= 0 || (product.has_variants && !variant)}
                className="btn-gold flex-1 rounded-sm py-4 text-sm font-semibold uppercase tracking-wider disabled:opacity-50"
                data-testid="detail-add-to-cart"
              >
                {size?.inventory <= 0 ? "Sold Out" : product.has_variants && !variant ? "Select a Variant" : "Add to Cart"}
              </button>
            </div>

            <div className="mt-4 flex items-center gap-2 font-mono text-xs text-muted">
              <ShieldCheck className="h-4 w-4 text-gold" /> Secure checkout · Card, Apple Pay & Google Pay
            </div>
            <div className="mt-2 flex items-center gap-2 text-xs text-muted" data-testid="pdp-processing-note">
              <Truck className="h-4 w-4 text-gold" /> Please allow 3–5 business days for order processing before shipment.
            </div>

            {/* details */}
            <div className="mt-10 space-y-6 border-t border-[#E4DDD0] pt-8">
              <Detail label="Best for">{product.targets}</Detail>
              <Detail label="Skin types">{product.skin_types.join(" · ")}</Detail>
              <Detail label="How to use">{product.how_to_use}</Detail>
              <Detail label="Origin">{product.origin}</Detail>
              <Detail label="Key ingredients">{product.key_ingredients.join(" · ")}</Detail>
            </div>
          </Reveal>
        </div>
      </div>

      <ProductReviews slug={slug} />

      <div className="mx-auto max-w-[1400px] px-5 sm:px-8 pb-16">
        <p className="border-t border-[#E4DDD0] pt-8 text-xs leading-relaxed text-muted/80" data-testid="product-disclaimer">
          {DISCLAIMER}
        </p>
      </div>
    </div>
  );
}

function Detail({ label, children }) {
  return (
    <div className="grid gap-1 sm:grid-cols-[160px_1fr]">
      <p className="font-mono text-xs uppercase tracking-wider text-gold">{label}</p>
      <p className="text-sm text-muted leading-relaxed">{children}</p>
    </div>
  );
}
